// Building blocks shared by the configuration panels (RF-CFG-01..14): secret state, test
// results with their evidence label, the token-consumption notice, origin badges and the
// form section frame. Every value shown is a server fact; absent facts are labelled.
import type { ReactNode } from "react";
import type { AgentRole, Role } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { useSession } from "../../session/SessionProvider.tsx";
import type { OperationState } from "../../lib/useOperation.ts";
import type { CapabilityState, ModelTarget, ProviderAdapter, SecretState } from "../../api/types-wave2.ts";
import { Pill } from "../../components/Status.tsx";
import { TextField } from "../../components/Field.tsx";
import { AlertTriangleIcon } from "../../components/icons.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { formatMs } from "../../lib/format.ts";

export const ADAPTERS = ["omniroute", "openrouter", "nvidia_nim", "jev", "openai_responses", "openai_chat", "integration_module"] as const satisfies readonly ProviderAdapter[];
export const CAPABILITY_KEYS = ["streaming", "tool_calling", "multi_turn_tools", "json_schema", "images", "prompt_cache", "reasoning_summary", "reasoning_effort"] as const;
export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];
export const PROTOCOL_IDS = ["openai_responses", "openai_chat_completions", "jev_decision", "ia_module"] as const satisfies readonly ModelTarget["protocol"][];
export const TARGET_KINDS = ["chat", "embedding", "rerank", "decision"] as const satisfies readonly ModelTarget["kind"][];
export const ROLE_IDS = ["maestro", "assistant", "planner", "classifier", "researcher", "builder", "tester", "critic", "integrator", "compactor"] as const satisfies readonly AgentRole[];

const ROLE_RANK: Record<Role, number> = { observer: 0, reviewer: 1, operator: 2, maintainer: 3, admin: 4 };

/** Organization role of the signed-in person (the API enforces it; the UI only avoids dead ends). */
export function useOrgRole(): { role: Role | null; atLeast: (min: Role) => boolean } {
  const { me } = useSession();
  const role = me?.role ?? null;
  return { role, atLeast: (min) => (role ? ROLE_RANK[role] >= ROLE_RANK[min] : false) };
}

export function FormSection({ title, description, children, testId }: { title: string; description?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <fieldset className="form-section" data-testid={testId}>
      <legend className="form-section-title">{title}</legend>
      {description ? <p className="field-hint form-section-desc">{description}</p> : null}
      <div className="form-grid">{children}</div>
    </fieldset>
  );
}

/** Masked secret state: never the value, only missing/active/revoked and the masked hint. */
export function SecretStateView({ secret, label }: { secret: SecretState; label?: string }) {
  const { t, formatDateTime } = useI18n();
  const tone = secret.state === "active" ? "success" : secret.state === "revoked" ? "danger" : "neutral";
  const icon = secret.state === "active" ? "check" : secret.state === "revoked" ? "ban" : "circle";
  return (
    <span className="row row--wrap secret-state" data-testid="secret-state" data-state={secret.state}>
      {label ? <span className="subtle">{label}</span> : null}
      <Pill tone={tone} icon={icon}>
        {t(`secret.${secret.state}`)}
      </Pill>
      {secret.state === "active" && secret.masked_hint ? <span className="mono badge">{secret.masked_hint}</span> : null}
      {secret.updated_at ? <span className="subtle">{formatDateTime(secret.updated_at)}</span> : null}
    </span>
  );
}

export function StateBadge({ state }: { state: string }) {
  const { t } = useI18n();
  const tone = ["active", "verified", "validated", "running", "healthy", "passed", "succeeded"].includes(state)
    ? "success"
    : ["auth_failed", "invalid", "failed", "crash_loop", "unavailable"].includes(state)
      ? "danger"
      : ["disabled", "stopped"].includes(state)
        ? "neutral"
        : ["draining", "degraded", "unverified", "draft"].includes(state)
          ? "warning"
          : "info";
  const icon = tone === "success" ? "check" : tone === "danger" ? "x" : tone === "warning" ? "alert" : tone === "neutral" ? "ban" : "circle";
  const key = `state.${state}` as MessageKey;
  const label = t(key);
  return (
    <Pill tone={tone} icon={icon}>
      {label === key ? state : label}
    </Pill>
  );
}

const ORIGIN_TONE: Record<string, "success" | "info" | "warning" | "neutral"> = {
  configured: "success",
  reported: "info",
  estimated: "warning",
  stale: "warning",
  unknown: "neutral",
  global: "info",
  project: "success",
  task: "success",
  unset: "neutral",
};

export function OriginBadge({ origin }: { origin: string }) {
  const { t } = useI18n();
  const key = `origin.value.${origin}` as MessageKey;
  const label = t(key);
  return (
    <span className={`badge origin-badge tone-${ORIGIN_TONE[origin] ?? "neutral"}`} data-origin={origin}>
      {label === key ? origin : label}
    </span>
  );
}

export function CapabilityChips({ declared, verified }: { declared: Partial<Record<string, CapabilityState>>; verified: Partial<Record<string, CapabilityState>> }) {
  const { t } = useI18n();
  const keys = CAPABILITY_KEYS.filter((k) => declared[k] !== undefined || verified[k] !== undefined);
  if (keys.length === 0) return <span className="subtle">{t("models.noCapabilities")}</span>;
  return (
    <ul className="chip-list" aria-label={t("models.capabilities")}>
      {keys.map((k) => {
        const v = verified[k];
        const d = declared[k];
        const state: CapabilityState = v === "verified" || v === "failed" ? v : (d ?? "unknown");
        return (
          <li key={k} className={`cap-chip cap-chip--${state}`} data-capability={k} data-state={state}>
            <span>{t(`cap.${k}`)}</span>
            <span className="cap-chip-state">{t(`capState.${state}`)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Explicit notice + mandatory budget for tests that send tokens (RF-CFG-10). */
export function TokenBudgetField({ value, onChange, error, id }: { value: string; onChange: (v: string) => void; error?: string | null; id?: string }) {
  const { t } = useI18n();
  return (
    <div className="stack token-notice" style={{ gap: 6 }} data-testid="token-notice" id={id}>
      <p className="row tone-warning" style={{ fontSize: "var(--text-sm)", alignItems: "flex-start" }}>
        <AlertTriangleIcon size={16} />
        <span>{t("tests.consumesTokens")}</span>
      </p>
      <TextField
        label={t("tests.maxCost")}
        hint={t("tests.maxCostHint")}
        type="number"
        min={0.01}
        step="0.01"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error ?? null}
        required
      />
    </div>
  );
}

/** Outcome of a provider test/probe operation, including the fixture evidence label. */
export function TestOutcome({ op, error, busy }: { op: OperationState | null; error: unknown; busy: boolean }) {
  const { t } = useI18n();
  if (error) return <ActionError error={error} />;
  if (!op) return null;
  const pending = op.status === "accepted" || op.status === "running";
  const result = (op.result ?? {}) as { status?: string; failure_class?: string | null; detail?: Record<string, unknown> };
  const detail = result.detail ?? {};
  const evidence = typeof detail["evidence_label"] === "string" ? detail["evidence_label"] : detail["fixture_only"] === true ? "fixture_only_not_homologated" : null;
  const status = result.status ?? null;
  const latency = typeof detail["latency_ms"] === "number" ? detail["latency_ms"] : null;
  const reason = typeof detail["reason"] === "string" ? detail["reason"] : null;
  return (
    <div className="test-outcome" role="status" aria-live="polite" data-testid="test-outcome" data-status={status ?? op.status}>
      {pending || busy ? (
        <Pill tone="info" icon="spinner">
          {t("tests.running")}
        </Pill>
      ) : op.error ? (
        <ActionError error={new Error(op.error.message)} title={t("tests.failedToRun")} />
      ) : (
        <div className="row row--wrap">
          <Pill tone={status === "passed" ? "success" : status === "failed" ? "danger" : "neutral"} icon={status === "passed" ? "check" : status === "failed" ? "x" : "circle"}>
            {status ? t(`testStatus.${status as "passed" | "failed" | "skipped" | "unknown"}`) : t("testStatus.unknown")}
          </Pill>
          {result.failure_class ? <span className="badge mono">{result.failure_class}</span> : null}
          {latency !== null ? <span className="subtle">{formatMs(latency)}</span> : null}
          {evidence ? (
            <span className="badge badge--warning" data-testid="evidence-label" title={t("tests.fixtureOnlyHint")}>
              {evidence === "fixture_only_not_homologated" ? t("tests.fixtureOnly") : evidence}
            </span>
          ) : null}
          {reason ? <span className="subtle user-content">{reason}</span> : null}
        </div>
      )}
    </div>
  );
}

/** Tri-state "is free": unknown is never shown as free (RF-CFG-04). */
export function freeLabel(isFree: boolean | null, t: (k: MessageKey) => string): string {
  return isFree === null ? t("models.freeUnknown") : isFree ? t("models.freeYes") : t("models.freeNo");
}
